import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setErr(res.error || "Помилка входу");
  };

  const fill = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left — form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <img src="/logo.png" alt="Global Vision" className="h-16 w-16 shrink-0 object-contain" />
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Global Vision</div>
              <div className="heading text-2xl font-bold text-[#0F2027] leading-none">Analytics</div>
            </div>
          </div>

          <h1 className="heading text-3xl lg:text-4xl font-bold text-[#0F2027] mb-2">
            Вхід до системи
          </h1>
          <p className="text-slate-500 mb-8 text-sm">
            Аналітика, операції, фінанси та управління персоналом мережі офтальмологічних центрів
            Global Vision.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#0F2027]">Логін</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="text"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="напр. А.С. або Кульчицька Л.Я."
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#0F2027]">Пароль</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-11 bg-[#0F2027] hover:bg-[#0F2027]/90 text-white"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Увійти
            </Button>
          </form>
        </div>
      </div>

      {/* Right — brand panel */}
      <div className="hidden lg:flex relative bg-[#0F2027] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1663151064065-cb334788f77d?crop=entropy&cs=srgb&fm=jpg&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2027]/90 via-[#0F2027]/80 to-[#0D9488]/60" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="text-sm uppercase tracking-[0.3em] text-teal-300">Внутрішня система</div>
          <div>
            <h2 className="heading text-5xl font-bold leading-tight mb-4">
              Єдиний центр<br />управління клінікою
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              Операції, заробітна плата, KPI, фінанси та аналітика —
              в одному місці. Готовність до інтеграції з OpticTrip Manager ERP.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="border-l-2 border-teal-400 pl-3">
              <div className="text-2xl font-bold heading">9</div>
              <div className="text-white/60">ролей доступу</div>
            </div>
            <div className="border-l-2 border-teal-400 pl-3">
              <div className="text-2xl font-bold heading">10+</div>
              <div className="text-white/60">модулів</div>
            </div>
            <div className="border-l-2 border-teal-400 pl-3">
              <div className="text-2xl font-bold heading">100%</div>
              <div className="text-white/60">прозорість</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
